import {
  Component,
  effect,
  Input,
  OnChanges,
  OnDestroy,
  signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {MatSort, MatSortModule, Sort} from '@angular/material/sort';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {firstValueFrom, Observable, of, Subject} from 'rxjs';
import {CommonDirectives} from '@app/shared/directives/common-directives';
import {Appointment, NgAppointments} from '@app/shared/interfaces/appointment.interface';
import {DashboardType} from '@app/core/enums/dashboard-types.enum';

import {MatDialog} from "@angular/material/dialog";
import {appointmentType} from "@app/shared/components/generic-dashboard/generic-dashboard.component";
import {PaginationComponent} from "@app/shared/components/pagination/pagination.component";
import {PageSearchSortOptions} from "@app/shared/interfaces/pageSearchSortOptions.interface";
import {AppointmentSearchParams} from "@app/shared/interfaces/appointmentSearchParams.interface";

import {
  AppointmentDetailModalComponent
} from "@app/shared/components/appointment-detail-modal/appointment-detail-modal.component";
import {rxResource} from "@angular/core/rxjs-interop";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import { Overlay } from '@angular/cdk/overlay';
import { tap } from "rxjs/operators";
import {MatButtonModule} from '@angular/material/button';
import {MatMenuModule} from '@angular/material/menu';
import {ToastService} from '@app/core/services/toast.service';
import {
  AppointmentExportFormat,
  exportAppointmentResults
} from '@app/shared/components/appointment-list/appointment-export';

interface ReferralAppointmentExportData {
  appointments: Appointment[];
  missingDetailIds: ReadonlySet<string>;
}

@Component({
  selector: 'app-appointment-list2',
  imports: [
    MatTableModule,
    MatSortModule,
    PaginationComponent,
    MatProgressSpinner,
    MatButtonModule,
    MatMenuModule
  ],
  templateUrl: './referral-appointment-list.component.html',
  styleUrl: './referral-appointment-list.component.scss'
})
export class ReferralAppointmentListComponent implements OnChanges, OnDestroy {
  protected readonly appointmentType = appointmentType;

  @ViewChild(MatSort) sort!: MatSort;
  @Input() title!: string;
  @Input() dashboardType?: DashboardType;
  @Input() showReferringProvider = false;
  @Input() showStatus = false;
  @Input() appointmentListType!: appointmentType;
  @Input() defaultSortField = 'appointmentDate';
  @Input() defaultSortDirection: 'asc'|'desc'  = 'asc';
  @Input() search = '';
  @Input({ required: true }) fetchAppointments$!: (filter: AppointmentSearchParams) => Observable<NgAppointments>;
  @Input({ required: true }) fetchAppointmentDetails$!: (ids: string[]) => Observable<NgAppointments>;


  // Maximum number of days to look for upcoming/searchable appointments
  private readonly maxFutureDays: number = 180;

  // Number of months to look for previous appointments and searchable appointments
  private readonly minPrevMonth: number = 6;
  private readonly exportPageSize = 1000;
  private readonly exportDetailBatchSize = 25;
  private readonly maxExportPages = 10;

  readonly defaultPageSize: number = 25;
  readonly isExporting = signal(false);
  totalPages: number = 0;

  $ngDestroy = new Subject();
  private readonly baseDisplayedColumns: string[] = ['date', 'name', 'dob', 'iowaOrthoProvider', 'location', 'visit' ];
  displayedColumns: string[] = [...this.baseDisplayedColumns];
  tableDataSource = new MatTableDataSource<Appointment>([]);

  emptyTableMessage: string = "No records found. Please adjust your filters or search criteria and try again.";

  fullDataSignal = signal<string>("");

  resourceSortOptions = signal<PageSearchSortOptions>({
    pageNumber: 1,
    pageSize: this.defaultPageSize,
    searchTerm: '',
    sortField: this.defaultSortField,
    sortDirection: this.defaultSortDirection,
    secondarySortField: "lastName",
    secondarySortDirection: "asc"
  });

  fullDataSource = rxResource<NgAppointments, string>({
    params: () => this.fullDataSignal(),
    stream: (params) => {
      const searchTerm = params.params.trim();
      if (this.appointmentListType == appointmentType.PatientSearch && searchTerm == '') {
        const searchData: NgAppointments = {
          items: [],
          count: 0
        }
        this.emptyTableMessage = 'Enter search criteria above to view results.';
        return of(searchData);
      }
      return this.fetchAppointments$(this.createAppointmentSearchParams(searchTerm)).pipe(
        tap(results => {
          results.items.sort((a, b) => this.sortAppointments(a, b))
        }))
    }});

  // Detail list dependent on the full data set, and the current page
  detailListResource = rxResource<NgAppointments, Appointment[] | undefined>({
    params: () => {
      var page = this.resourceSortOptions();
      const primaryData = this.fullDataSource.value();
      if (!primaryData) return undefined;
      let startIndex = (page.pageNumber - 1) * page.pageSize;
      return primaryData.items.slice(startIndex, startIndex + page.pageSize);
    },
    stream: ({ params }) => {
      if (params.length === 0) {
        return of({items: [], count: 0});
      }
      return this.fetchAppointmentDetails$(params.map(e => e.id));
    }
  });

   constructor(
    private dialog: MatDialog,
    private overlay: Overlay,
    private toastService: ToastService
  ) {
    effect(() => {
      // FullDataSet
      const dataForPage = this.fullDataSource.value();
      if(dataForPage) {
        this.totalPages = Math.ceil(dataForPage.count / this.defaultPageSize);
      }

      //Sorted Data
      const data = this.detailListResource.value();
      if (data) {
        this.tableDataSource.data = data.items;
      } else {
        this.tableDataSource.data = [];
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showReferringProvider'] || changes['showStatus']) {
      this.syncDisplayedColumns();
    }

    if(changes['search']) {
      this.onPageChange(1);
      this.fullDataSignal.update((_) => this.search);
    }
  }

  ngOnDestroy(): void {
    this.$ngDestroy.next(true);
  }


  sortAppointments(a: Appointment, b: Appointment) {
    let primaryCompare = 0;
    let secondaryCompare = 0;
    switch (this.sort.active) {
      case 'date':
        primaryCompare = new Date(a.date + " " + a.time).getTime() - new Date(b.date + " " + b.time).getTime();
        secondaryCompare = (a.lastName ?? '').localeCompare(b.lastName ?? '');
        break;
      case 'name':
        primaryCompare = (a.lastName ?? '').localeCompare(b.lastName ?? '');
        if(primaryCompare === 0) secondaryCompare = new Date(a.date + " " + a.time).getTime() - new Date(b.date + " " + b.time).getTime();
        break;
      case 'location':
        primaryCompare = (a.location ?? '').localeCompare(b.location ?? '');
        if(primaryCompare === 0) secondaryCompare = new Date(a.date + " " + a.time).getTime() - new Date(b.date + " " + b.time).getTime();
        break;
      case 'visit':
        primaryCompare = (a.type ?? '').localeCompare(b.type ?? '');
        if(primaryCompare === 0) secondaryCompare = new Date(a.date + " " + a.time).getTime() - new Date(b.date + " " + b.time).getTime();
        break;
      case 'status':
        primaryCompare = (a.status ?? '').localeCompare(b.status ?? '');
        if(primaryCompare === 0) secondaryCompare = new Date(a.date + " " + a.time).getTime() - new Date(b.date + " " + b.time).getTime();
        break;
      case 'iowaOrthoProvider':
        primaryCompare = (a.renderingProviderLastName ?? '').localeCompare(b.renderingProviderLastName ?? '');
        if(primaryCompare === 0) secondaryCompare = new Date(a.date + " " + a.time).getTime() - new Date(b.date + " " + b.time).getTime();
        break;
      case 'referringProvider':
        primaryCompare = (a.referringProviderLastName ?? '').localeCompare(b.referringProviderLastName ?? '');
        if(primaryCompare === 0) secondaryCompare = new Date(a.date + " " + a.time).getTime() - new Date(b.date + " " + b.time).getTime();
        break;
      default:
        const aValue = (a as any)[this.sort.active];
        const bValue = (b as any)[this.sort.active];
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          primaryCompare = aValue.localeCompare(bValue);
        } else {
          primaryCompare = (aValue > bValue) ? 1 : (aValue < bValue) ? -1 : 0;
        }
    }
    // If items are not same based on primary sort then return that comparison and don't apply secondary sort
    if (primaryCompare !== 0) { return this.sort.direction === "asc" ? primaryCompare : -primaryCompare; }
    if (secondaryCompare !== 0) { return secondaryCompare }
    return (a.lastName ?? '').localeCompare(b.lastName ?? '');
  };


  public onSortChange(sort: Sort){
    const direction = sort.direction;
    if (direction !== 'asc' && direction !== 'desc') {
      return;
    }
    this.fullDataSource.update((appointments) => {
      appointments?.items.sort((a, b) => this.sortAppointments(a, b));
      return {...appointments} as NgAppointments;
    });
    console.log(this.fullDataSource.value()?.items.slice(0, 25));
  }

  onRowClicked(row: any) {
    this.openAppointmentInfoModal(row.id, row.patient);
  }

  private syncDisplayedColumns(): void {
    if(this.showStatus){
        this.displayedColumns = [...this.baseDisplayedColumns, 'status'];
    }

    if(this.showReferringProvider && this.displayedColumns.length > 5){
      this.displayedColumns.splice(5, 0, 'referringProvider');
    }
  }
  getDisplayedColumns(): string[] {
    return this.displayedColumns;
  }

  onPageChange(newPage: number) {
    this.resourceSortOptions.update((current) => ({...current, pageNumber: newPage}));
  }

  async exportAppointments(format: AppointmentExportFormat): Promise<void> {
    if (this.isExporting() || !this.fullDataSource.value()?.count) {
      return;
    }

    this.isExporting.set(true);
    try {
      const {appointments, missingDetailIds} = await this.fetchAllAppointmentsForExport();
      if (appointments.length === 0) {
        this.toastService.showWarningToast('There are no appointments to export.');
        return;
      }

      await exportAppointmentResults(appointments, format, {
        title: this.title,
        showReferringProvider: this.showReferringProvider,
        showStatus: this.showStatus,
        missingDetailIds
      });
      if (missingDetailIds.size > 0) {
        this.toastService.showWarningToast(
          `${missingDetailIds.size} appointment${missingDetailIds.size === 1 ? '' : 's'} with missing details encountered.`
        );
      }
    } catch (error: unknown) {
      console.error('Unable to export appointments.', error);
      this.toastService.showErrorToast('Unable to export appointments. Please try again.');
    } finally {
      this.isExporting.set(false);
    }
  }

  private createAppointmentSearchParams(
    searchTerm: string,
    pageNumber = 1,
    pageSize = this.exportPageSize,
    referenceDate = new Date()
  ): AppointmentSearchParams {
    const currentDate = referenceDate.toISOString().slice(0, 19);
    const minDate = new Date(referenceDate);
    minDate.setMonth(minDate.getMonth() - this.minPrevMonth);
    const maxDate = new Date(referenceDate);
    maxDate.setDate(maxDate.getDate() + this.maxFutureDays);

    const dateRange = this.appointmentListType === appointmentType.FutureAppointments
      ? {minDate: currentDate, maxDate: maxDate.toISOString().slice(0, 19)}
      : this.appointmentListType === appointmentType.PastAppointments
        ? {minDate: minDate.toISOString().slice(0, 19), maxDate: currentDate}
        : {minDate: minDate.toISOString().slice(0, 19), maxDate: maxDate.toISOString().slice(0, 19)};

    return {
      ...dateRange,
      page: pageNumber.toString(),
      limit: pageSize.toString(),
      searchText: searchTerm.trim()
    };
  }

  private async fetchAllAppointmentsForExport(): Promise<ReferralAppointmentExportData> {
    const referenceDate = new Date();
    const searchTerm = this.fullDataSignal();
    const firstPage = await firstValueFrom(this.fetchAppointments$(
      this.createAppointmentSearchParams(searchTerm, 1, this.exportPageSize, referenceDate)
    ));
    const summaries = [...firstPage.items];
    const pageCount = Math.ceil(firstPage.count / this.exportPageSize);

    for (let pageNumber = 2; pageNumber <= pageCount; pageNumber++) {
      const page = await firstValueFrom(this.fetchAppointments$(
        this.createAppointmentSearchParams(searchTerm, pageNumber, this.exportPageSize, referenceDate)
      ));
      summaries.push(...page.items);
    }
    summaries.sort((a, b) => this.sortAppointments(a, b));
    const exportSummaries = summaries.slice(0, this.defaultPageSize * this.maxExportPages);

    const detailsById = new Map<string, Appointment>();
    for (let start = 0; start < exportSummaries.length; start += this.exportDetailBatchSize) {
      const batch = exportSummaries.slice(start, start + this.exportDetailBatchSize);
      const details = await firstValueFrom(this.fetchAppointmentDetails$(batch.map((appointment) => appointment.id)));
      details.items.forEach((appointment) => detailsById.set(appointment.id, appointment));
    }

    const appointmentsMissingDetails = exportSummaries.filter((appointment) => !detailsById.has(appointment.id));
    const missingDetailIds = new Set(appointmentsMissingDetails.map((appointment) => appointment.id));
    if (appointmentsMissingDetails.length > 0) {
      console.log('Appointments missing details during export:', appointmentsMissingDetails);
    }

    return {
      appointments: exportSummaries.map((appointment) => detailsById.get(appointment.id) ?? appointment),
      missingDetailIds
    };
  }

  openAppointmentInfoModal(appointmentId: string | null, patient: any) {
    if (!appointmentId) { return; }
    const isReferralAppointmentsModal = this.dashboardType === DashboardType.Referral;

    const dialogRef = this.dialog.open(AppointmentDetailModalComponent, {
      width: '700px',
      autoFocus: false,
      disableClose: false,
      scrollStrategy: this.overlay.scrollStrategies.block()
    });

    dialogRef.componentInstance.appointmentDetailParams = {
      patientName: `${patient.firstName} ${patient.lastName}`,
      dateOfBirth: patient.dateOfBirth,
      appointmentId: appointmentId,
      appointmentType: this.appointmentListType,
      ...(isReferralAppointmentsModal ? { showDocumentsTable: true } : {})
    }

    dialogRef.componentInstance.detailSignal.set({
      appointmentId: appointmentId,
      patientId: patient.id,
      caseNumber: '',
      appointmentType: this.appointmentListType
    });
  }
}
